import { useCallback, useEffect, useState } from 'react';
import type { HardwareAsset, UpsertHardwareAssetInput } from '@ddp/shared';

import { apiFetch } from '../../api/client.js';

/**
 * Flotte de matériels (ADR-0029). Liste des matériels de l'organisation + écritures (créer, mettre à
 * jour, retirer). Ressource transverse : aucun `projectId`. Chaque écriture rafraîchit la liste — le
 * registre reste modeste, on ne cherche pas ici la finesse d'un cache incrémental.
 */
export function useFleet() {
  const [assets, setAssets] = useState<HardwareAsset[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      setError(null);
      setAssets(await apiFetch<HardwareAsset[]>('/hardware'));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Chargement impossible');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const create = useCallback(
    async (input: UpsertHardwareAssetInput) => {
      await apiFetch('/hardware', { method: 'POST', body: input });
      await refresh();
    },
    [refresh],
  );

  const update = useCallback(
    async (id: string, input: UpsertHardwareAssetInput) => {
      await apiFetch(`/hardware/${id}`, { method: 'PUT', body: input });
      await refresh();
    },
    [refresh],
  );

  const remove = useCallback(
    async (id: string) => {
      await apiFetch(`/hardware/${id}`, { method: 'DELETE' });
      await refresh();
    },
    [refresh],
  );

  return { assets, loading, error, create, update, remove };
}
